import React, { useEffect, useState } from 'react';
import { Card, Table, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faPlay, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { getTopCharts, recordPlay } from '../../services/exploreApi';
import { getSongShareUrl } from '../../utils/shareLinks';
import Pagination from '../common/Pagination';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardFooter,
} from '../common/ListMobileLayout';

const ExploreTopCharts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [simulateTaskId, setSimulateTaskId] = useState('');
  const [simulateLoading, setSimulateLoading] = useState(false);
  const [simulateMessage, setSimulateMessage] = useState(null);

  const fetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTopCharts({ period, page, items_per_page: pageSize });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, page]);

  const handleSimulatePlay = async (e) => {
    e.preventDefault();
    if (!simulateTaskId.trim()) return;
    setSimulateLoading(true);
    setSimulateMessage(null);
    try {
      await recordPlay({ task_id: simulateTaskId.trim(), source: 'explore' });
      setSimulateMessage({ type: 'success', text: 'Play recorded' });
      setSimulateTaskId('');
      fetch();
    } catch (err) {
      setSimulateMessage({ type: 'danger', text: err.response?.data?.message || err.message });
    } finally {
      setSimulateLoading(false);
    }
  };

  const songs = data?.data?.songs || data?.data || [];
  const total = data?.objects?.total ?? data?.objects?.count ?? songs.length;

  const openShare = (taskId) => {
    const shareUrl = getSongShareUrl(taskId);
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="dashboard-list-page">
      <h2 className="mb-4">
        <FontAwesomeIcon icon={faTrophy} className="me-2" />
        Top Charts
      </h2>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Form.Select className="form-control form-select" style={{ width: 'auto' }} value={period} onChange={(e) => { setPeriod(e.target.value); setPage(1); }}>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="all_time">All Time</option>
          </Form.Select>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : songs.length === 0 ? (
            <p className="text-muted text-center py-4">No data yet</p>
          ) : (
            <>
              <ListDesktop>
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Task ID</th>
                      <th>Title</th>
                      <th>Creator</th>
                      <th>Plays</th>
                      <th>Genre</th>
                      <th style={{ width: 90 }}>Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {songs.map((s, idx) => {
                      const shareUrl = getSongShareUrl(s.task_id);
                      return (
                        <tr key={s.task_id || idx}>
                          <td><strong>{s.rank ?? idx + 1}</strong></td>
                          <td><code>{s.task_id}</code></td>
                          <td>{s.title || '—'}</td>
                          <td>{s.creator_display_name || s.creator_user_id || '—'}</td>
                          <td>{s.play_count ?? 0}</td>
                          <td>{s.genre || '—'}</td>
                          <td>
                            {shareUrl ? (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                title="Open song player in new tab"
                                onClick={() => openShare(s.task_id)}
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                              </Button>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </ListDesktop>

              <ListMobile>
                {songs.map((s, idx) => {
                  const rank = s.rank ?? idx + 1;
                  const shareUrl = getSongShareUrl(s.task_id);
                  return (
                    <MobileCard key={s.task_id || idx}>
                      <MobileCardTop>
                        <Badge bg="primary" className="me-2">#{rank}</Badge>
                        <MobileCardTitle>{s.title || '—'}</MobileCardTitle>
                      </MobileCardTop>
                      <MobileCardRow label="Task ID" mono>
                        {s.task_id || '—'}
                      </MobileCardRow>
                      <MobileCardRow label="Creator">
                        {s.creator_display_name || s.creator_user_id || '—'}
                      </MobileCardRow>
                      <MobileCardRow label="Plays">{s.play_count ?? 0}</MobileCardRow>
                      <MobileCardRow label="Genre">{s.genre || '—'}</MobileCardRow>
                      {shareUrl && (
                        <MobileCardFooter>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            title="Open song player in new tab"
                            onClick={() => openShare(s.task_id)}
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                            Open
                          </Button>
                        </MobileCardFooter>
                      )}
                    </MobileCard>
                  );
                })}
              </ListMobile>

              <Pagination currentPage={page} totalItems={total} itemsPerPage={pageSize} onPageChange={setPage} />
            </>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Simulate play (testing)</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSimulatePlay} className="row g-3 align-items-end">
            <div className="col-md-4">
              <Form.Label>Task ID</Form.Label>
              <Form.Control
                className="form-control"
                value={simulateTaskId}
                onChange={(e) => setSimulateTaskId(e.target.value)}
                placeholder="task-xxx"
                disabled={simulateLoading}
              />
            </div>
            <div className="col-md-2">
              <Button type="submit" variant="primary" disabled={simulateLoading || !simulateTaskId.trim()}>
                {simulateLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faPlay} className="me-1" />Record play</>}
              </Button>
            </div>
          </Form>
          {simulateMessage && <Alert variant={simulateMessage.type} className="mt-2 mb-0 py-2">{simulateMessage.text}</Alert>}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ExploreTopCharts;
